// "Add your menu" — the provider's half of the WP3 loop, mounted on #/edit for
// food & drink types only (pilot scope, decision 061).
//
// Photograph the menu → it uploads → the Edge Function reads it → the items
// appear below, marked "waiting for review". Nothing the provider sees here is
// live yet, and the UI says so plainly: over-promising is how a provider ends up
// believing a price is showing to riders when John has not looked at it.

import { uploadMenuPhoto } from './media-upload';
import {
  distillMenuPhoto,
  listMyMenuRows,
  centsToEuroText,
  type MenuRow,
} from './menu-api';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
function esc(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

export interface MenuSectionOptions {
  container: HTMLElement;
  providerId: string;
  userId: string;
}

const STATUS_LABEL: Record<MenuRow['review_status'], string> = {
  unreviewed: 'Waiting for review',
  approved: 'Live',
  rejected: 'Not used',
};

function priceText(row: MenuRow): string {
  if (row.price_cents === null) return '—';
  const low = centsToEuroText(row.price_cents);
  const cur = row.currency === 'EUR' ? '€' : `${row.currency ?? ''} `;
  if (row.price_cents_max !== null && row.price_cents_max !== row.price_cents) {
    return `${cur}${low}–${centsToEuroText(row.price_cents_max)}`;
  }
  return `${cur}${low}`;
}

export function mountMenuSection(opts: MenuSectionOptions): void {
  const { container, providerId, userId } = opts;
  container.className = 'field-section menu-section';
  container.innerHTML = `
    <h2>Your menu</h2>
    <p class="muted small">
      Photograph your menu and we'll read the dishes and prices from it. John checks
      everything before it reaches travellers — nothing goes live automatically.
    </p>
    <p class="consent-line">
      Photos you add here are shown to travellers in the Curate app, and the prices we read
      from them are shown once John has checked them. Only upload a menu you are happy to
      publish, and that is yours to publish.
    </p>
    <div class="menu-drop">
      <label class="menu-btn" for="menu-file">Photograph or choose a menu</label>
      <input type="file" id="menu-file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden />
      <p class="muted small">Lay it flat, fill the frame, good light, straight on.</p>
    </div>
    <p id="menu-status" class="status" aria-live="polite"></p>
    <div id="menu-rows"></div>
  `;

  const fileInput = container.querySelector<HTMLInputElement>('#menu-file')!;
  const status = container.querySelector<HTMLElement>('#menu-status')!;
  const rowsEl = container.querySelector<HTMLElement>('#menu-rows')!;

  const setStatus = (msg: string, kind: '' | 'success' | 'error'): void => {
    status.textContent = msg;
    status.className = kind ? `status status-${kind}` : 'status';
  };

  const renderRows = (rows: MenuRow[]): void => {
    if (rows.length === 0) {
      rowsEl.innerHTML = '<p class="muted small">No menu items yet.</p>';
      return;
    }
    const waiting = rows.filter((r) => r.review_status === 'unreviewed').length;
    rowsEl.innerHTML = `
      ${waiting > 0 ? `<p class="muted small">${waiting} item${waiting === 1 ? '' : 's'} waiting for review.</p>` : ''}
      <table class="menu-table">
        <thead><tr><th>Item</th><th>Price</th><th>Status</th></tr></thead>
        <tbody>
          ${rows.map((r) => `
            <tr class="menu-${r.review_status}">
              <td>
                <strong>${esc(r.item)}</strong>
                ${r.section ? `<span class="muted small"> · ${esc(r.section)}</span>` : ''}
                ${r.description ? `<div class="muted small">${esc(r.description)}</div>` : ''}
              </td>
              <td>${esc(priceText(r))}</td>
              <td><span class="menu-badge menu-badge-${r.review_status}">${STATUS_LABEL[r.review_status]}</span></td>
            </tr>`).join('')}
        </tbody>
      </table>`;
  };

  const refresh = async (): Promise<void> => {
    try {
      renderRows(await listMyMenuRows(providerId));
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not load your menu.', 'error');
    }
  };

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    fileInput.value = '';  // let the same file be re-chosen after a failure
    try {
      setStatus('Uploading…', '');
      const url = await uploadMenuPhoto(file, userId, (f) =>
        setStatus(`Uploading… ${Math.round(f * 100)}%`, ''),
      );
      setStatus('Reading the menu — this takes a few seconds…', '');
      const result = await distillMenuPhoto(providerId, url);
      // 'refused' is a normal outcome: the model looked and honestly found no
      // menu. Show its retry advice as guidance, not as a failure.
      setStatus(result.message, result.status === 'refused' ? 'error' : 'success');
      await refresh();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Something went wrong. Try again.', 'error');
    }
  });

  void refresh();
}
